import { SAMPLE_FIELD_LABELS } from "@/lib/constants/sample-field-labels";
import { MAX_BULK_SAMPLE_IDS } from "@/lib/constants/samples";
import { uuidSchema } from "@/lib/validation/schemas/common";
import { z } from "zod";

/**
 * Sample Validation Schemas
 *
 * Shared field validators used by both the creation modal and any future
 * sample edit forms. Backend validation (FastAPI/Pydantic) is the source of
 * truth; these schemas exist for client-side UX only.
 *
 * SamplingMethod enum (OpenAPI v2.3.67):
 * Stockpiles | Storage Bin | Conveyor Belt | Mini Stockpiles/Sample Pads |
 * Truck Dump | Graded Base Material | Surface | Transportation Unit |
 * Automatic | Other
 *
 * SampleMassUnit / Unit enum: g | kg | lb
 */

// =============================================================================
// Shared Field Validators
// =============================================================================

/** OpenAPI SamplingMethod enum values (v2.3.67). */
export const SAMPLING_METHOD_VALUES = [
  "Stockpiles",
  "Storage Bin",
  "Conveyor Belt",
  "Mini Stockpiles/Sample Pads",
  "Truck Dump",
  "Graded Base Material",
  "Surface",
  "Transportation Unit",
  "Automatic",
  "Other",
] as const;

export const samplingMethodSchema = z.enum(SAMPLING_METHOD_VALUES);

const sampleNameField = z
  .string()
  .trim()
  .min(1, "Sample name is required")
  .max(255, "Sample name must be less than 255 characters");

const locationIdField = z
  .string()
  .trim()
  .min(1, `${SAMPLE_FIELD_LABELS.locationFallback} is required`);

const aggregateIdField = z.string().trim().min(1, "Aggregate is required");

const sampledByField = z.string().trim().min(1, "Sampled By is required");

/**
 * Required in the web form for UX; OpenAPI SampleCreate marks it optional.
 * Kept as string.min(1) so RHF can use "" as the empty default before submit;
 * values are constrained to SAMPLING_METHOD_VALUES at submit / by the API.
 */
const samplingMethodField = z
  .string()
  .trim()
  .min(1, "Sampling method is required");

const samplingDatetimeField = z
  .string()
  .trim()
  .min(1, `${SAMPLE_FIELD_LABELS.samplingDate} is required`);

const sampleWeightField = z
  .string()
  .optional()
  .refine(
    (val) => {
      if (!val) return true;
      const num = Number.parseFloat(val);
      return Number.isFinite(num) && num >= 0 && num <= 100000;
    },
    {
      message: `${SAMPLE_FIELD_LABELS.sampleWeight} must be a positive number less than 100,000`,
    },
  );

/** OpenAPI Unit enum. */
const sampleWeightUnitField = z.enum(["g", "kg", "lb"]).optional();

const notesField = z
  .string()
  .max(1000, "Notes must be less than 1000 characters")
  .optional();

// =============================================================================
// Sample Creation / Edit Schema (single source of truth)
// =============================================================================

export const sampleCreationSchema = z
  .object({
    sample_name: sampleNameField,
    location_id: locationIdField,
    aggregate_id: aggregateIdField,
    sampling_method: samplingMethodField,
    sampling_by_user_id: sampledByField,
    sampling_datetime: samplingDatetimeField,
    weight_of_sample: sampleWeightField,
    weight_of_sample_unit: sampleWeightUnitField,
    notes: notesField,
  })
  .superRefine((data, ctx) => {
    if (data.weight_of_sample && !data.weight_of_sample_unit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${SAMPLE_FIELD_LABELS.sampleWeight} unit is required when sample weight is provided`,
        path: ["weight_of_sample_unit"],
      });
    }
  });

export type SampleCreationForm = z.infer<typeof sampleCreationSchema>;

// =============================================================================
// Bulk details update (PATCH /api/v1/samples/list)
// =============================================================================

const PATCH_FIELD_KEYS = [
  "location_id",
  "aggregate_id",
  "sampling_by_user_id",
  "notes",
  "weight_of_sample",
  "weight_of_sample_unit",
  "sampling_method",
] as const;

function refineBulkPatchFields(
  data: {
    location_id?: string;
    aggregate_id?: string;
    weight_of_sample?: number | string;
    weight_of_sample_unit?: string;
  },
  ctx: z.RefinementCtx,
  hasAnyPatchField: boolean,
  emptyPatchPath: PropertyKey[] = ["sample_ids"],
) {
  if (!hasAnyPatchField) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "At least one sample field must be provided for update",
      path: emptyPatchPath,
    });
  }

  const hasLocation = Boolean(data.location_id);
  const hasAggregate = Boolean(data.aggregate_id);
  if (hasLocation !== hasAggregate) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Facility and aggregate must be set together",
      path: hasLocation ? ["aggregate_id"] : ["location_id"],
    });
  }

  if (data.weight_of_sample && !data.weight_of_sample_unit) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${SAMPLE_FIELD_LABELS.sampleWeight} unit is required when sample weight is provided`,
      path: ["weight_of_sample_unit"],
    });
  }
}

/** API payload for PATCH /api/v1/samples/list (OpenAPI SampleBulkDetailsUpdate). */
export const sampleBulkDetailsUpdateSchema = z
  .object({
    sample_ids: z
      .array(uuidSchema)
      .min(1, "At least one sample ID is required")
      .max(
        MAX_BULK_SAMPLE_IDS,
        `Cannot update more than ${MAX_BULK_SAMPLE_IDS} samples at once`,
      )
      .refine((ids) => new Set(ids).size === ids.length, {
        message: "Duplicate sample IDs are not allowed",
      }),
    location_id: uuidSchema.optional(),
    aggregate_id: uuidSchema.optional(),
    sampling_by_user_id: uuidSchema.optional(),
    notes: z
      .string()
      .max(1000, "Notes must be less than 1000 characters")
      .optional(),
    weight_of_sample: z.number().min(0).optional(),
    weight_of_sample_unit: z.enum(["g", "kg", "lb"]).optional(),
    sampling_method: samplingMethodSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const hasAnyPatchField = PATCH_FIELD_KEYS.some(
      (key) => data[key] !== undefined,
    );
    refineBulkPatchFields(data, ctx, hasAnyPatchField);
  });

export type SampleBulkDetailsUpdateInput = z.infer<
  typeof sampleBulkDetailsUpdateSchema
>;

/** Bulk-edit modal form: empty string means leave the field unchanged. */
export const sampleBulkEditFormSchema = z
  .object({
    location_id: z.string(),
    aggregate_id: z.string(),
    sampling_by_user_id: z.string(),
    sampling_method: z.string(),
    weight_of_sample: sampleWeightField,
    weight_of_sample_unit: sampleWeightUnitField,
    notes: notesField,
  })
  .superRefine((data, ctx) => {
    const hasAnyPatchField =
      Boolean(data.location_id) ||
      Boolean(data.aggregate_id) ||
      Boolean(data.sampling_by_user_id) ||
      Boolean(data.sampling_method) ||
      Boolean(data.weight_of_sample) ||
      Boolean(data.notes?.trim());
    refineBulkPatchFields(data, ctx, hasAnyPatchField, []);
  });

export type SampleBulkEditForm = z.infer<typeof sampleBulkEditFormSchema>;
